/** Protected sources: texts that must never be reproduced; publication is blocked when too similar. */

import {
  type KnownSource,
  useAcademySourcesCreate,
  useAcademySourcesDestroy,
  useAcademySourcesList,
  useAcademySourcesPartialUpdate,
} from "@agencyos/api-client";
import { Button, GlassCard, GlassSheet, Skeleton, TextAreaField, TextField } from "@agencyos/ui-web";
import { Plus, Trash2 } from "lucide-react";
import { type FormEvent, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { fieldErrors } from "../../lib/settings";
import { dateText } from "../billing/shared";
import { ErrorList, Pager, useAcademyRoles, useRefreshAcademy } from "./shared";

function SourceSheet({ source, onClose }: { source: KnownSource | undefined; onClose: () => void }) {
  const { t } = useTranslation();
  const { canEdit } = useAcademyRoles();
  const refresh = useRefreshAcademy();
  const create = useAcademySourcesCreate({ mutation: { onSuccess: refresh } });
  const update = useAcademySourcesPartialUpdate({ mutation: { onSuccess: refresh } });
  const destroy = useAcademySourcesDestroy({ mutation: { onSuccess: refresh } });
  const [title, setTitle] = useState(source?.title ?? "");
  const [text, setText] = useState(source?.text ?? "");
  const [confirming, setConfirming] = useState(false);
  const saveError = source ? update.error : create.error;
  const errors = fieldErrors(saveError);
  const pending = create.isPending || update.isPending || destroy.isPending;

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    const data = { title: title.trim(), text };
    if (source) update.mutate({ id: source.id, data }, { onSuccess: (r) => r.status === 200 && onClose() });
    else create.mutate({ data }, { onSuccess: (r) => r.status === 201 && onClose() });
  };

  return (
    <GlassSheet open title={source ? t("academyAdmin.sources.edit") : t("academyAdmin.sources.new")} onClose={onClose}>
      <form className="stack" onSubmit={onSubmit} noValidate>
        <p className="muted">{t("academyAdmin.sources.sheetHint")}</p>
        <fieldset className="plain-fieldset stack" disabled={!canEdit}>
          <TextField label={t("academyAdmin.sources.titleLabel")} value={title} maxLength={200} error={errors.title} onChange={(e) => setTitle(e.target.value)} />
          <TextAreaField label={t("academyAdmin.sources.text")} lang="fr" rows={10} value={text} error={errors.text} onChange={(e) => setText(e.target.value)} />
          {saveError && !errors.title && !errors.text ? <ErrorList error={saveError} /> : null}
          <ErrorList error={destroy.error} />
          <div className="row">
            <Button type="submit" disabled={pending}>
              {pending ? t("common.loading") : source ? t("common.save") : t("academyAdmin.create")}
            </Button>
            {source ? (
              confirming ? (
                <>
                  <Button
                    variant="secondary"
                    icon={<Trash2 size={16} aria-hidden="true" />}
                    disabled={pending}
                    onClick={() => destroy.mutate({ id: source.id }, { onSuccess: onClose })}
                  >
                    {t("academyAdmin.sources.confirmDelete")}
                  </Button>
                  <Button variant="ghost" onClick={() => setConfirming(false)}>
                    {t("common.cancel")}
                  </Button>
                </>
              ) : (
                <Button variant="ghost" icon={<Trash2 size={16} aria-hidden="true" />} onClick={() => setConfirming(true)}>
                  {t("academyAdmin.sources.delete")}
                </Button>
              )
            ) : null}
          </div>
        </fieldset>
      </form>
    </GlassSheet>
  );
}

export function SourcesTab() {
  const { t, i18n } = useTranslation();
  const { canEdit } = useAcademyRoles();
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<KnownSource | "new" | null>(null);
  const close = useCallback(() => setEditing(null), []);
  const list = useAcademySourcesList({ page });
  const data = list.data?.status === 200 ? list.data.data : undefined;
  return (
    <GlassCard as="section" className="stack">
      <div className="section-title">
        <div>
          <h2>{t("academyAdmin.sources.title")}</h2>
          <p className="muted">{t("academyAdmin.sources.intro")}</p>
        </div>
        {canEdit ? (
          <Button icon={<Plus size={16} aria-hidden="true" />} onClick={() => setEditing("new")}>
            {t("academyAdmin.sources.new")}
          </Button>
        ) : null}
      </div>
      {data === undefined ? (
        <Skeleton style={{ height: 160 }} />
      ) : data.results.length ? (
        <>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>{t("academyAdmin.sources.titleLabel")}</th>
                  <th>{t("academyAdmin.sources.excerpt")}</th>
                  <th>{t("academyAdmin.sources.added")}</th>
                </tr>
              </thead>
              <tbody>
                {data.results.map((s) => (
                  <tr key={s.id}>
                    <td>
                      <button type="button" className="link" onClick={() => setEditing(s)}>
                        {s.title}
                      </button>
                    </td>
                    <td className="muted academy-clamp" lang="fr">
                      {s.text}
                    </td>
                    <td className="nowrap">{dateText(s.created_at, i18n.language)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager page={page} hasNext={Boolean(data.next)} onChange={setPage} />
        </>
      ) : (
        <p className="muted">{t("academyAdmin.sources.empty")}</p>
      )}
      {editing ? <SourceSheet key={editing === "new" ? "new" : editing.id} source={editing === "new" ? undefined : editing} onClose={close} /> : null}
    </GlassCard>
  );
}
